import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { apiKeysApi, apiKeysOptions } from '@/pages/Profile/TokenGeneration/api';
import type { ApiKeyOutput } from '@/pages/Profile/TokenGeneration/api';
import { downloadAsCsv } from '@/utils/downloadAsCsv';

import { createNewKeyName } from './createNewApiKeyName';

const useGenerationBody = () => {
  const { formatDateString } = useDateText();
  const [apiKeyName, setApiKeyName] = useState('');
  const [formError, setFormError] = useState('');
  const [generated, setGenerated] = useState(false);
  const [newApiKey, setNewApiKey] = useState<ApiKeyOutput | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!apiKeyName)
      setApiKeyName(`my_apikey_${formatDateString(new Date().toJSON(), DateTextVariants.FULL)}`);
  }, [formatDateString]);

  const {
    mutate,
    isPending: loading,
    isError
  } = useMutation({
    mutationFn: async (initialName: string): Promise<ApiKeyOutput> => {
      let keyName = initialName;
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          return await apiKeysApi.create(keyName);
        } catch (err) {
          if (
            attempt < 2 &&
            err instanceof AxiosError &&
            err.response?.data?.errors?.[0]?.includes('already exists')
          ) {
            const keys = queryClient.getQueryData<ApiKeyOutput[]>(apiKeysOptions.all()) ?? [];
            keyName = createNewKeyName(keyName, keys);
          } else {
            throw err;
          }
        }
      }
      throw new Error('Could not generate a unique key name after 3 attempts');
    },
    onSuccess: key => {
      queryClient.setQueryData<ApiKeyOutput[]>(apiKeysOptions.all(), old =>
        old ? [...old, key] : [key]
      );
      setNewApiKey(key);
      setGenerated(true);
    },
    onError: error => {
      if (error instanceof AxiosError) {
        setFormError(error.response?.data?.errors?.[0] ?? 'An unknown error occurred');
      }
    }
  });

  const exportToCsv = () => {
    const header = 'Name,Key';
    const token = newApiKey?.prefixedRawApiKey ?? '';
    downloadAsCsv([header, `${newApiKey?.label},${token}`]);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (formError) setFormError('');
    setApiKeyName(event.target.value);
  };

  const onGenerate = () => {
    mutate(apiKeyName);
  };

  const handleClose = (setOpen: (state: boolean) => void) => {
    setGenerated(false);
    setNewApiKey(null);
    setOpen(false);
  };

  return {
    handleChange,
    apiKeyName,
    onGenerate,
    exportToCsv,
    handleClose,
    generated,
    error: formError,
    loading,
    isError,
    newApiKey
  };
};

export default useGenerationBody;
